/**
 * English: Single task detail view with polling for live updates.
 * Thai: มุมมองรายละเอียดงานเดียว พร้อม polling สำหรับอัปเดตสด
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTaskStatus } from '../hooks/useTaskStatus';
import { useApi } from '../hooks/useApi';
import TaskDetail from '../components/tasks/TaskDetail';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import JsonViewer from '../components/shared/JsonViewer';
import StatusBadge from '../components/shared/StatusBadge';
import { ENDPOINTS } from '../config/api';
import { FullTaskState, TaskRun } from '../types';
import PageShell from '../components/layout/PageShell';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set(['completed', 'error', 'cancelled']);

function mapToFullTaskState(raw: Record<string, unknown>): FullTaskState {
  return {
    ...raw,
    task_status: (raw.task_status ?? raw.status) as string | undefined,
  };
}

export default function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const { taskState, loading, error, fetchStatus } = useTaskStatus();
  const { get } = useApi();
  const { userOrgName } = useAuth();
  const [archivedRun, setArchivedRun] = useState<TaskRun | null>(null);
  const [resolved, setResolved] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const tryArchived = useCallback(async () => {
    if (!taskId) return null;
    try {
      const params: Record<string, string> = userOrgName ? { org_name: userOrgName } : {};
      const row = (await get(ENDPOINTS.taskRun(taskId), params)) as TaskRun;
      setArchivedRun(row);
      return row;
    } catch {
      setArchivedRun(null);
      return null;
    }
  }, [get, taskId, userOrgName]);

  useEffect(() => {
    if (!taskId) return;

    const poll = async () => {
      const state = await fetchStatus(taskId);
      if (state) {
        setResolved(true);
        setArchivedRun(null);
        const statusVal =
          (state as unknown as Record<string, string>).task_status || (state as { status?: string }).status;
        if (TERMINAL_STATUSES.has(String(statusVal))) {
          stopPolling();
        }
        return;
      }
      stopPolling();
      setArchiveLoading(true);
      try {
        const row = await tryArchived();
        if (!row) {
          setArchivedRun(null);
        }
      } finally {
        setArchiveLoading(false);
        setResolved(true);
      }
    };

    setResolved(false);
    setArchiveLoading(false);
    void poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return stopPolling;
  }, [taskId, fetchStatus, tryArchived]);

  const fullState = taskState ? mapToFullTaskState(taskState as unknown as Record<string, unknown>) : null;
  const isTerminal = fullState && TERMINAL_STATUSES.has(fullState.task_status || '');

  const showLive = Boolean(taskId && taskState);
  const showArchived = Boolean(resolved && !taskState && archivedRun);
  const showMissing = Boolean(resolved && !taskState && !archivedRun);
  const showSpinner = !resolved && (loading || archiveLoading);

  return (
    <PageShell narrow>
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/tasks" className="text-xs text-app-accent hover:underline">
          ← Task History
        </Link>
        <span className="text-app-text-dim">/</span>
        <Link to="/" className="text-xs text-app-text-dim hover:text-app-text transition-colors">
          Dashboard
        </Link>
        <span className="text-app-text-dim">/</span>
        <h1 className="text-sm font-medium text-app-text font-mono truncate min-w-0 flex-1">
          {taskId}
        </h1>
        {showLive && !isTerminal && (
          <span className="flex items-center gap-1.5 text-xs text-app-text-dim">
            <LoadingSpinner size="sm" /> polling
          </span>
        )}
      </div>

      {showSpinner ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : null}

      {showMissing ? (
        <div className="bg-app-surface border border-app-border rounded-xl p-6 text-center space-y-2">
          <p className="text-sm font-medium text-app-text">Task not found</p>
          <p className="text-xs text-app-text-dim max-w-md mx-auto">
            This task is not in live memory and has no saved row in task history (invalid ID, or persistence
            failed when the task finished).
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs">
            <Link to="/tasks" className="text-app-accent hover:underline">
              Task History
            </Link>
            <Link to="/chat-test" className="text-app-accent hover:underline">
              Chat Test
            </Link>
          </div>
        </div>
      ) : null}

      {showArchived && archivedRun ? (
        <div className="space-y-4">
          <p className="text-xs text-app-text-dim">
            This task finished and was removed from live memory — showing the persisted snapshot from PostgreSQL.
          </p>
          <div className="bg-app-surface border border-app-border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-sm font-medium text-app-text">Archived run</h2>
              <StatusBadge variant={archivedRun.status === 'completed' ? 'success' : archivedRun.status === 'error' ? 'error' : 'muted'} dot>
                {archivedRun.status}
              </StatusBadge>
            </div>
            {archivedRun.prompt ? (
              <div>
                <p className="text-[10px] font-medium text-app-text-dim uppercase">Prompt</p>
                <p className="text-sm text-app-text whitespace-pre-wrap">{archivedRun.prompt}</p>
              </div>
            ) : null}
            {archivedRun.summary ? (
              <div>
                <p className="text-[10px] font-medium text-app-text-dim uppercase">Summary / result</p>
                <p className="text-sm text-app-text whitespace-pre-wrap">{archivedRun.summary}</p>
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-2 text-xs text-app-text-sec">
              <p>
                <span className="text-app-text-dim">Station:</span>{' '}
                <span className="font-mono">{archivedRun.station_id || '—'}</span>
              </p>
              <p>
                <span className="text-app-text-dim">Mode:</span> {archivedRun.mode || '—'}
              </p>
              <p>
                <span className="text-app-text-dim">Messages:</span> {archivedRun.message_count}
              </p>
              <p>
                <span className="text-app-text-dim">Finished:</span>{' '}
                {new Date(archivedRun.finished_at).toLocaleString()}
              </p>
            </div>
            <JsonViewer data={archivedRun} />
          </div>
        </div>
      ) : null}

      {showLive ? <TaskDetail taskId={taskId!} fullState={fullState} /> : null}

      {error && showMissing ? (
        <p className="text-xs text-app-text-dim text-center">{error}</p>
      ) : null}
    </PageShell>
  );
}
